import {
  TableCellType,
  TableHeader,
} from "../../../components/custom-table/table";
import {
  CaseAppealListRow,
  CaseAppealsTableRowData,
} from "../../../data-model/caseAppealsTable";
import {
  TableCellMultiline,
  TableCellWithBadge,
} from "../../../data-model/tableCells";
import { OrderStatus } from "../../../enums/authStatus";
import { BadgeColor, BadgeTextColor } from "../../../enums/coloredText";
import { capitalizeString } from "../../../utils/stringModifications";
import getPoc from "../../callingWorklist/table/utils/getPoc";
import { getStpFileStatus } from "../../pharmaStpFile/table/utils/getCorrectStatus";
import {
  CaseAppealsTableColumnKeys,
  CaseAppealsTableHeaderLabels,
} from "../enums/caseAppealsTableColumnKeys";
import { extractOncologyBatchFromFileName } from "./utils/extractOncologyFileInfo";
import {
  formatCoverMyMedsId,
  getAppealProbabilityBadge,
  getSecondStpStatusBadge,
} from "./utils/formatCaseAppealsCells";
import {
  formatDobToDisplay,
  formatTimestampToDisplay,
} from "./utils/formatDobToDisplay";
import { getAppealStatusBadgeStyles } from "./utils/getAppealBadgeStyles";

export const CaseAppealsTableHeader: TableHeader[] = [
  {
    label: CaseAppealsTableHeaderLabels.DATE_OF_WORK,
    key: CaseAppealsTableColumnKeys.DATE_OF_WORK,
    order: 1,
    width: 10,
    sortable: true,
    type: TableCellType.MULTILINE,
  },
  {
    label: CaseAppealsTableHeaderLabels.PATIENT_NAME,
    key: CaseAppealsTableColumnKeys.PATIENT_NAME,
    order: 2,
    width: 12,
    sortable: false,
    type: TableCellType.MULTILINE,
  },
  {
    label: CaseAppealsTableHeaderLabels.DOB,
    key: CaseAppealsTableColumnKeys.DOB,
    order: 3,
    width: 8,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: CaseAppealsTableHeaderLabels.DRUG,
    key: CaseAppealsTableColumnKeys.DRUG,
    order: 4,
    width: 12,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: CaseAppealsTableHeaderLabels.COVER_MY_MEDS_ID,
    key: CaseAppealsTableColumnKeys.COVER_MY_MEDS_ID,
    order: 5,
    width: 10,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: CaseAppealsTableHeaderLabels.SECOND_STP_STATUS,
    key: CaseAppealsTableColumnKeys.SECOND_STP_STATUS,
    order: 6,
    width: 10,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: CaseAppealsTableHeaderLabels.RESPONSE_STATUS,
    key: CaseAppealsTableColumnKeys.RESPONSE_STATUS,
    order: 7,
    width: 10,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: CaseAppealsTableHeaderLabels.CASE_ID,
    key: CaseAppealsTableColumnKeys.CASE_ID,
    order: 8,
    width: 8,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: CaseAppealsTableHeaderLabels.POC,
    key: CaseAppealsTableColumnKeys.POC,
    order: 9,
    width: 7,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: CaseAppealsTableHeaderLabels.CASE_STATUS,
    key: CaseAppealsTableColumnKeys.CASE_STATUS,
    order: 10,
    width: 8,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: CaseAppealsTableHeaderLabels.APPEAL_PROBABILITY,
    key: CaseAppealsTableColumnKeys.APPEAL_PROBABILITY,
    order: 11,
    width: 9,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: CaseAppealsTableHeaderLabels.ACTION,
    key: CaseAppealsTableColumnKeys.ACTION,
    order: 12,
    width: 6,
    sortable: false,
    type: TableCellType.ADD_KEY_BUTTON,
  },
  {
    label: CaseAppealsTableHeaderLabels.NEXT_FOLLOW_UP,
    key: CaseAppealsTableColumnKeys.NEXT_FOLLOW_UP,
    order: 13,
    width: 9,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: CaseAppealsTableHeaderLabels.HISTORY_ICON,
    key: CaseAppealsTableColumnKeys.HISTORY_ICON,
    order: 14,
    width: 4,
    sortable: false,
    type: TableCellType.CALLING_HISTORY_ICON,
  },
];

/**
 * Maps appeal list API rows into the Case Appeals table display model.
 *
 * @param data - Rows from `pharmacy/appeal/list`
 * @returns Rows formatted for `CustomTable`
 */
export const generateTableDataForCaseAppeals = (
  data: CaseAppealListRow[],
): CaseAppealsTableRowData[] => {
  return data.map((item) => {
    const responseStatusBadge: TableCellWithBadge = item.response_status
      ? getStpFileStatus(item.response_status)
      : {
          text: "--",
          color: BadgeTextColor[OrderStatus.NO_STATUS],
          bgColor: BadgeColor[OrderStatus.NO_STATUS],
          hoverText: "--",
        };

    const patientName: TableCellMultiline = {
      mainText: capitalizeString(item.patient_name ?? undefined) || "--",
      secondaryText: `MRN : ${item.patient_mrn ?? "--"}`,
      hideCopyIcon: true,
    };

    const actionLabel = item.covermymed_id ? "Edit" : "Add";
    const cmmId = formatCoverMyMedsId(item.covermymed_id);
    const batchFromFile = extractOncologyBatchFromFileName(item.filename ?? "");
    const dow: TableCellMultiline = {
      mainText: formatTimestampToDisplay(item.dumped_at),
      secondaryText: `Batch : ${
        batchFromFile && batchFromFile !== "--" ? batchFromFile : "--"
      }`,
      hideCopyIcon: true,
    };

    return {
      identifier: item.identifier,
      appeal_id: item.appeal_id ?? null,
      patient_name: item.patient_name ?? null,
      patient_mrn: item.patient_mrn ?? null,
      dob: item.dob ?? null,
      drug: item.drug ?? null,
      covermymed_id: item.covermymed_id ?? null,
      second_stp_status: item.second_stp_status ?? null,
      response_status: item.response_status ?? null,
      appeal_type: item.appeal_type ?? null,
      appeal_status: item.appeal_status ?? null,
      appeal_reason: item.appeal_reason ?? null,
      appeal_comment: item.appeal_comment ?? null,
      appeal_poc: item.appeal_poc ?? null,
      appeal_probability: item.appeal_probability ?? null,
      appeal_band: item.appeal_band ?? null,
      appeal_sent_at: item.appeal_sent_at ?? null,
      appeal_updated_at: item.appeal_updated_at ?? null,
      appeal_case_id: item.appeal_case_id ?? null,
      appeal_follow_up_date: item.appeal_follow_up_date ?? null,
      filename: item.filename ?? null,
      dumped_at: item.dumped_at ?? null,
      pa_outcome_provider_summary: item.pa_outcome_provider_summary ?? null,
      [CaseAppealsTableColumnKeys.DATE_OF_WORK]: dow,
      [CaseAppealsTableColumnKeys.PATIENT_NAME]: patientName,
      [CaseAppealsTableColumnKeys.DOB]: formatDobToDisplay(item.dob) || "--",
      [CaseAppealsTableColumnKeys.DRUG]: item.drug ?? "--",
      [CaseAppealsTableColumnKeys.COVER_MY_MEDS_ID]: cmmId || "--",
      [CaseAppealsTableColumnKeys.SECOND_STP_STATUS]: getSecondStpStatusBadge(
        item.second_stp_status,
      ),
      [CaseAppealsTableColumnKeys.RESPONSE_STATUS]: responseStatusBadge,
      [CaseAppealsTableColumnKeys.CASE_ID]: item.appeal_case_id?.trim() || "",
      [CaseAppealsTableColumnKeys.POC]: getPoc(item.appeal_poc ?? ""),
      [CaseAppealsTableColumnKeys.CASE_STATUS]: getAppealStatusBadgeStyles(
        item.appeal_status,
      ),
      [CaseAppealsTableColumnKeys.APPEAL_PROBABILITY]:
        getAppealProbabilityBadge(item.appeal_probability, item.appeal_band),
      [CaseAppealsTableColumnKeys.ACTION]: {
        value: "Add",
        isDisabled: false,
        title: actionLabel,
      },
      [CaseAppealsTableColumnKeys.NEXT_FOLLOW_UP]: formatDobToDisplay(
        item.appeal_follow_up_date,
      ),
    };
  });
};
